import { ErrorModal } from "@/components/shared/modal/error-modal";
import Modal from "@/components/shared/modal/modal";
import { useVendorsContext } from "@/pages/configurations/vendors/context/VendorsContext";
import {
	Button,
	CircularProgress,
	FormGroup,
	FormLabel,
	Grid,
	MenuItem,
	Snackbar,
	Switch,
	TextField,
	Theme,
	Typography,
	createStyles,
	makeStyles,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import React, { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { INITIAL_VALUES } from "../../models/FormInitialValues";
import { Part } from "../../models/Part";
import { getPartById, updatePart } from "../../repositories/PartsRepository";

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		modalBody: {},
		title: {
			fontSize: 15,
		},
		chip: {
			margin: 2,
		},
		errorLabel: {
			fontSize: 12,
			color: "#F44336",
		},
	}),
);

type Props = {
	partIdForEdit: number | null;
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
};

export const EditPart = ({ partIdForEdit, show, onHide }: Props) => {
	const classes = useStyles();
	const [openAlertMessage, setOpenAlertMessage] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [message, setMessage] = useState("");
	const [messageType, setMessageType] = useState("");

	const [partForEdit, setPartForEdit] = useState<Part | null>(null);

	const [error, setError] = useState<string | null>(null);
	const [showErrorModal, setShowErrorModal] = useState(false);

	const {
		state: { vendors },
	} = useVendorsContext();

	const sortedVendors = [...vendors].sort((a, b) =>
		a.vendorName.localeCompare(b.vendorName),
	);

	const {
		control,
		setValue,
		handleSubmit,
		formState: { errors },
	} = useForm<Part>({ defaultValues: INITIAL_VALUES });

	const handleClose = (event?: React.SyntheticEvent, reason?: string) => {
		if (reason === "clickaway") {
			return;
		}
		setOpenAlertMessage(false);
	};

	const onSubmit: SubmitHandler<Part> = async (data) => {
		setIsSubmitLoading(true);
		try {
			const res = await updatePart(partIdForEdit, data);
			if (res?.ok) {
				setMessage("The part was updated successfully");
				setMessageType("success");
				onHide(true);
			}
		} catch (e) {
			setMessage(`Error, please try again`);
			setMessageType("error");

			setError(e as string);
			setShowErrorModal(true);
		}
		setIsSubmitLoading(false);
		setOpenAlertMessage(true);
	};

	useEffect(() => {
		if (show) {
			(async () => {
				setIsLoading(true);
				const part = await getPartById(partIdForEdit);
				if (part.data?.data?.success) {
					setPartForEdit(part.data?.data?.result);
				} else {
					onHide(true);
					setMessage("Error, part not found. Try again");
					setOpenAlertMessage(true);
					setPartForEdit(null);
				}

				setIsLoading(false);
			})();
		}
	}, [show]);

	useEffect(() => {
		if (show && partForEdit) {
			const {
				partId,
				partName,
				description,
				sku,
				unit,
				vendorId,
				actualArrival,
				isActive,
			} = partForEdit!;
			setValue("partId", partId);
			setValue("partName", partName);
			setValue("description", description);
			setValue("sku", sku);
			setValue("unit", unit);
			setValue("vendorId", vendorId);
			setValue("actualArrival", actualArrival);
			setValue("isActive", isActive);
		}
	}, [partForEdit]);

	return (
		<>
			<>
				<Modal
					open={show}
					handleClose={() => {
						onHide(true);
					}}
					id="editPart"
					title="Edit part"
					maxWidth="md"
				>
					<form onSubmit={handleSubmit(onSubmit)}>
						<Modal.Content
							className={classes.modalBody}
							style={{ padding: "15px 30px" }}
						>
							{isLoading && (
								<Grid
									container
									justifyContent="center"
									alignItems="center"
									style={{ minHeight: "390px" }}
								>
									{/* <LazyLoading /> */}
									<CircularProgress size="4rem" />
								</Grid>
							)}

							{!isLoading && (
								<>
									<Grid container spacing={3}>
										<Grid item md={12} xs={12}>
											<Controller
												name="partName"
												rules={{
													required: true,
													validate: {
														notBlank: (v) => `${v}`.trim().length > 0,
													},
												}}
												control={control}
												render={({ field }) => (
													<TextField
														label="Name"
														value={field.value}
														onChange={field.onChange}
														variant="outlined"
														error={!!errors.partName}
														fullWidth
														margin="dense"
													/>
												)}
											/>

											{errors.partName && (
												<span className={classes.errorLabel}>
													Name is required
												</span>
											)}
										</Grid>
										<Grid item md={12} xs={12}>
											<Controller
												name="description"
												rules={{
													required: true,
													validate: {
														notBlank: (v) => `${v}`.trim().length > 0,
													},
												}}
												control={control}
												render={({ field }) => (
													<TextField
														label="Description"
														value={field.value}
														onChange={field.onChange}
														variant="outlined"
														error={!!errors.description}
														fullWidth
														margin="dense"
													/>
												)}
											/>

											{errors.description && (
												<span className={classes.errorLabel}>
													Description is required
												</span>
											)}
										</Grid>
										<Grid item md={12} xs={12}>
											<Controller
												name="sku"
												rules={{
													required: true,
													validate: {
														notBlank: (v) => `${v}`.trim().length > 0,
													},
												}}
												control={control}
												render={({ field }) => (
													<TextField
														label="SKU"
														value={field.value}
														onChange={field.onChange}
														variant="outlined"
														error={!!errors.sku}
														fullWidth
														margin="dense"
													/>
												)}
											/>

											{errors.sku && (
												<span className={classes.errorLabel}>
													SKU is required
												</span>
											)}
										</Grid>
										<Grid item md={12} xs={12}>
											<Controller
												name="unit"
												rules={{
													required: true,
													validate: {
														notBlank: (v) => `${v}`.trim().length > 0,
													},
												}}
												control={control}
												render={({ field }) => (
													<TextField
														label="Unit"
														value={field.value}
														onChange={field.onChange}
														variant="outlined"
														error={!!errors.unit}
														fullWidth
														margin="dense"
													/>
												)}
											/>

											{errors.unit && (
												<span className={classes.errorLabel}>
													Unit is required
												</span>
											)}
										</Grid>
										<Grid item md={6} xs={12}>
											<Controller
												name="vendorId"
												control={control}
												render={({ field }) => (
													<TextField
														select
														label="Vendor"
														value={field.value}
														onChange={field.onChange}
														variant="outlined"
														fullWidth
														margin="dense"
													>
														{sortedVendors.map((type) => (
															<MenuItem
																value={type.vendorId}
																key={type.vendorId}
															>
																{type.vendorName}
															</MenuItem>
														))}
													</TextField>
												)}
											/>
										</Grid>
										<Grid item md={4} xs={12}>
											<Typography>Status</Typography>
											<Controller
												name="isActive"
												control={control}
												render={({ field }) => (
													<FormGroup>
														<Switch
															// className='switchBase'
															color="primary"
															defaultChecked={field.value}
															value={field.value}
															onChange={field.onChange}
														/>

														<FormLabel component="legend">
															{field.value ? "Active" : "Inactive"}
														</FormLabel>
													</FormGroup>
												)}
											/>
										</Grid>
									</Grid>
								</>
							)}
						</Modal.Content>
						<Modal.Actions style={{ padding: "1rem" }}>
							<Grid container spacing={2} justifyContent="flex-end">
								<Grid item md={3} xs={12} style={{ margin: 0 }}>
									<Button
										fullWidth
										variant="contained"
										color="default"
										onClick={() => {
											onHide(false);
											setPartForEdit(null);
										}}
									>
										Cancel
									</Button>
								</Grid>
								<Grid item md={3} xs={12} style={{ margin: 0 }}>
									<Button
										fullWidth
										startIcon={
											isSubmitLoading ? <CircularProgress size="1rem" /> : <></>
										}
										disabled={isSubmitLoading}
										variant="contained"
										color="primary"
										type="submit"
									>
										Save
									</Button>
								</Grid>
							</Grid>
						</Modal.Actions>
					</form>
				</Modal>
				<Snackbar
					open={openAlertMessage}
					autoHideDuration={2500}
					onClose={handleClose}
				>
					{messageType == "success" ? (
						<Alert severity="success" onClose={handleClose}>
							{message}
						</Alert>
					) : (
						<Alert severity="error" onClose={handleClose}>
							{message}
						</Alert>
					)}
				</Snackbar>
			</>
			<ErrorModal onHide={() => setError(null)} error={error} />
		</>
	);
};
